import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik, FormikProps, useField } from 'formik';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import wordCount from 'word-count';
import * as yup from 'yup';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { IconInfoCircle } from '@tabler/icons-react';

import { SelectDropdown, Toggle, Tooltip } from '@zendesk/forethought-elements';
import { getSupportedChannels } from '../constants';
import FormInputFormat from '../FormInputFormat';
import { useKnowledgeCoachingBrandGroups } from '../hooks';
import KnowledgeTextField from '../KnowledgeTextField';
import debounce from 'lodash/fp/debounce';
import isEqual from 'lodash/fp/isEqual';
import ConfirmModal from 'src/components/email-builder/components/ConfirmModal';
import {
  useDiscardInstructionDraftMutation,
  useGetInstructionsQuery,
  usePublishInstructionMutation,
  useUpdateInstructionMutation,
} from 'src/services/ai-instruction/aiInstructionApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { AiInstruction } from 'src/types/aiInstructionTypes';
import { extractCvIdsFromFieldValue } from 'src/utils/cleanStr';
import { formatProductLabel } from 'src/utils/formatProductChannels';

export type EditingAiInstruction = Omit<
  AiInstruction,
  'created_date' | 'modified_date' | 'version' | 'last_modified_by'
>;

const WORD_LIMIT = 200;
const MAX_AI_INSTRUCTIONS_WITH_CVS = 2;

const createEditingAiInstructionSchema = ({
  allowAddingContextVariables,
  doesExistingInstructionHaveCvs,
  numAiInstructionsWithContextVariables,
  t,
}: {
  allowAddingContextVariables: boolean;
  doesExistingInstructionHaveCvs: boolean;
  numAiInstructionsWithContextVariables: number;
  t: TFunction<'knowledge'>;
}) => {
  return yup.object().shape({
    ai_instruction_id: yup.string().ensure().defined(),
    brands: yup.array().of(yup.string().required()).nullable(),
    instructions: yup
      .string()
      .required(t('validation.coachingRequired'))
      .trim()
      .test(
        'instruction-word-count',
        t('validation.coachingWordCount', { limit: WORD_LIMIT }),
        value => {
          if (!value) {
            return false;
          }

          const countWords = wordCount(value);

          return countWords >= 1 && countWords < WORD_LIMIT;
        },
      )
      .test(
        'context-variable-action-limit',
        t('validation.coachingActionLimit', {
          count: MAX_AI_INSTRUCTIONS_WITH_CVS,
        }),
        function (value) {
          if (!allowAddingContextVariables || !value) {
            return true;
          }

          const currentCvIds = extractCvIdsFromFieldValue(value);
          const currentInstructionHasCvs = currentCvIds.length > 0;

          // If not adding CVs to current instruction, no validation needed
          if (!currentInstructionHasCvs) {
            return true;
          }

          // Calculate the effective count:
          // If editing an existing instruction that already had CVs, exclude it from the count
          const effectiveCount = doesExistingInstructionHaveCvs
            ? numAiInstructionsWithContextVariables - 1
            : numAiInstructionsWithContextVariables;

          // Check if adding CVs would exceed the limit
          if (effectiveCount >= MAX_AI_INSTRUCTIONS_WITH_CVS) {
            return false;
          }

          return true;
        },
      ),
    is_active: yup.boolean().required(),
    name: yup
      .string()
      .required(t('validation.nameRequired'))
      .min(2, t('validation.nameMin'))
      .max(199, t('validation.nameMax')),
  });
};

const EMPTY_AI_INSTRUCTION: EditingAiInstruction = {
  ai_instruction_id: '',
  brands: [],
  instructions: '',
  is_active: true,
  is_draft: true,
  name: '',
  products: [],
  unsaved_changes: false,
};

export interface HeaderProps extends FormikProps<EditingAiInstruction> {
  existingInstruction?: EditingAiInstruction;
  isLoadingDebounce?: boolean;
  onClickDelete?: (id: string) => void;
  onClose: () => void;
}

export interface FooterProps extends FormikProps<EditingAiInstruction> {
  isLoadingDebounce?: boolean;
  marginTop?: string;
  onClose: () => void;
  publishCacheKey?: string;
}

export type SidebarProps = FormikProps<EditingAiInstruction> & {
  isLoadingDebounce?: boolean;
};

interface AiInstructionFormProps {
  allowAddingContextVariables?: boolean;
  containerSx?: SxProps<Theme>;
  existingInstruction?: EditingAiInstruction;
  Footer?: React.ComponentType<React.PropsWithChildren<FooterProps>>;
  FooterProps?: { marginTop?: string };
  Header?: React.ComponentType<React.PropsWithChildren<HeaderProps>>;
  numAiInstructionsWithContextVariables?: number;
  onClickDelete?: (idToDelete: string) => void;
  onSuccess: () => void;
  publishCacheKey?: string;
  shouldEditBrands?: boolean;
  shouldEditProducts?: boolean;
  Sidebar?: React.ComponentType<React.PropsWithChildren<SidebarProps>>;
}

export const AiInstructionForm = ({
  allowAddingContextVariables = false,
  containerSx,
  existingInstruction,
  Footer,
  FooterProps = {},
  Header,
  numAiInstructionsWithContextVariables = 0,
  onClickDelete,
  onSuccess,
  publishCacheKey = 'instruction-form-publish',
  shouldEditBrands = true,
  shouldEditProducts = true,
  Sidebar,
}: AiInstructionFormProps) => {
  const { t } = useTranslation('knowledge');
  const dispatch = useAppDispatch();
  const { data: publishedInstructions = [] } = useGetInstructionsQuery();
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isLoadingDebounce, setIsLoadingDebounce] = useState(false);
  const [updateInstruction] = useUpdateInstructionMutation({
    fixedCacheKey: 'instruction-form',
  });
  const [publishInstruction] = usePublishInstructionMutation({
    fixedCacheKey: publishCacheKey,
  });
  const [discardInstructionDraft, { isLoading: isLoadingDiscard }] =
    useDiscardInstructionDraftMutation({
      fixedCacheKey: 'instruction-form',
    });

  const publishedInstruction = useMemo(() => {
    return publishedInstructions.find(
      instruction =>
        instruction.ai_instruction_id ===
        existingInstruction?.ai_instruction_id,
    );
  }, [existingInstruction?.ai_instruction_id, publishedInstructions]);

  const isNew = !publishedInstruction;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAutoSave = useCallback(
    debounce(1000, async (currentValues: EditingAiInstruction) => {
      if (currentValues && currentValues.ai_instruction_id) {
        try {
          await updateInstruction({
            aiInstructionId: currentValues.ai_instruction_id,
            body: { ...currentValues }, // Send all current values
          });
        } finally {
          setIsLoadingDebounce(false);
        }
      } else {
        setIsLoadingDebounce(false);
      }
    }),
    [updateInstruction],
  );

  const handleClose = useCallback(
    (formikProps: FormikProps<EditingAiInstruction>) => {
      const {
        brands,
        instructions,
        is_active: isActive,
        name,
      } = formikProps.values;
      const published = publishedInstruction
        ? {
            brands: publishedInstruction.brands,
            instructions: publishedInstruction.instructions,
            is_active: publishedInstruction.is_active,
            name: publishedInstruction.name,
          }
        : null;
      const current = { brands, instructions, is_active: isActive, name };
      const hasBeenEdited = !isEqual(current, published);
      if (hasBeenEdited) {
        setIsDiscardModalOpen(true);
      } else {
        onSuccess();
      }
    },
    [onSuccess, publishedInstruction],
  );

  const handleDiscard = useCallback(
    async (formikProps: FormikProps<EditingAiInstruction>) => {
      if (formikProps?.values.ai_instruction_id) {
        try {
          await discardInstructionDraft({
            aiInstructionId: formikProps?.values.ai_instruction_id,
          }).unwrap();

          dispatch(
            setGlobalToastOptions({
              autoHideDuration: 3000,
              title: t('aiInstruction.toast.changesDiscarded'),
              variant: 'main',
            }),
          );
        } catch (error) {
          dispatch(
            setGlobalToastOptions({
              autoHideDuration: 3000,
              title: t('aiInstruction.toast.discardError'),
              variant: 'danger',
            }),
          );
        }
      }

      setIsDiscardModalOpen(false);
      onSuccess();
    },
    [discardInstructionDraft, dispatch, onSuccess, t],
  );

  const doesPublishedInstructionHaveCvs = useMemo(() => {
    if (!publishedInstruction?.instructions) {
      return false;
    }
    const cvIds = extractCvIdsFromFieldValue(publishedInstruction.instructions);
    return cvIds.length > 0;
  }, [publishedInstruction?.instructions]);

  const validationSchema = useMemo(
    () =>
      createEditingAiInstructionSchema({
        allowAddingContextVariables,
        doesExistingInstructionHaveCvs: doesPublishedInstructionHaveCvs,
        numAiInstructionsWithContextVariables,
        t,
      }),
    [
      allowAddingContextVariables,
      doesPublishedInstructionHaveCvs,
      numAiInstructionsWithContextVariables,
      t,
    ],
  );

  return (
    <Formik
      initialValues={existingInstruction ?? EMPTY_AI_INSTRUCTION}
      onSubmit={async values => {
        try {
          await publishInstruction({
            aiInstructionId: values.ai_instruction_id,
          }).unwrap();
          dispatch(
            setGlobalToastOptions({
              autoHideDuration: 3000,
              title: t('aiInstruction.toast.saveSuccess', {
                name: values.name,
              }),
              variant: 'main',
            }),
          );
          onSuccess();
        } catch (error) {
          dispatch(
            setGlobalToastOptions({
              autoHideDuration: 3000,
              title: t('aiInstruction.toast.saveError'),
              variant: 'danger',
            }),
          );
        }
      }}
      validateOnMount
      validationSchema={validationSchema}
    >
      {formikProps => {
        const { dirty, values } = formikProps;
        // formik calls children as a function
        // this is not conditional so it works fine
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (dirty) {
            setIsLoadingDebounce(true);
            debouncedAutoSave(values);
          }

          return () => {
            debouncedAutoSave.cancel();
          };
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [values, dirty, debouncedAutoSave]);

        return (
          <>
            {Header && (
              <Header
                {...formikProps}
                existingInstruction={existingInstruction}
                isLoadingDebounce={isLoadingDebounce}
                onClickDelete={onClickDelete}
                onClose={() => handleClose(formikProps)}
              />
            )}
            {Sidebar ? (
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: '1fr 2fr',
                  height: '100%',
                  overflow: 'auto',
                  py: 3,
                }}
              >
                {Sidebar && (
                  <Sidebar
                    {...formikProps}
                    isLoadingDebounce={isLoadingDebounce}
                  />
                )}
                <Box
                  sx={[
                    {
                      flexDirection: 'column',
                      px: 5,
                      py: 3,
                    },
                    ...(Array.isArray(containerSx)
                      ? containerSx
                      : [containerSx]),
                  ]}
                >
                  <Form
                    allowAddingContextVariables={allowAddingContextVariables}
                    isNew={isNew}
                    shouldEditBrands={shouldEditBrands}
                    shouldEditProducts={shouldEditProducts}
                  />
                </Box>
                {Footer && (
                  <Footer
                    {...FooterProps}
                    {...formikProps}
                    isLoadingDebounce={isLoadingDebounce}
                    onClose={() => handleClose(formikProps)}
                    publishCacheKey={publishCacheKey}
                  />
                )}
              </Box>
            ) : (
              <>
                <Box
                  sx={[
                    {
                      flexDirection: 'column',
                      mt: 3,
                      px: 5,
                      py: 3,
                    },
                    ...(Array.isArray(containerSx)
                      ? containerSx
                      : [containerSx]),
                  ]}
                >
                  <Form
                    allowAddingContextVariables={allowAddingContextVariables}
                    isNew={isNew}
                    shouldEditBrands={shouldEditBrands}
                    shouldEditProducts={shouldEditProducts}
                  />
                </Box>
                {Footer && (
                  <Footer
                    {...FooterProps}
                    {...formikProps}
                    isLoadingDebounce={isLoadingDebounce}
                    onClose={() => handleClose(formikProps)}
                    publishCacheKey={publishCacheKey}
                  />
                )}
              </>
            )}
            <ConfirmModal
              actions={[
                {
                  onClick: () => setIsDiscardModalOpen(false),
                  title: t('discardDialog.noStay'),
                },
                {
                  isDisabled: isLoadingDiscard,
                  isLoading: isLoadingDiscard,
                  onClick: () => handleDiscard(formikProps),
                  title: t('discardDialog.yesClose'),
                },
              ]}
              description={t('discardDialog.description')}
              isOpen={isDiscardModalOpen}
              onClose={() => setIsDiscardModalOpen(false)}
              title={t('discardDialog.title')}
            />
          </>
        );
      }}
    </Formik>
  );
};

const Form = ({
  allowAddingContextVariables,
  isNew,
  shouldEditBrands,
  shouldEditProducts,
}: {
  allowAddingContextVariables: boolean;
  isNew: boolean;
  shouldEditBrands: boolean;
  shouldEditProducts: boolean;
}) => {
  const { t } = useTranslation('knowledge');
  const { brandGroups, isVoiceKnowledgeCoachingEnabled } =
    useKnowledgeCoachingBrandGroups();
  const supportedChannels = getSupportedChannels({
    isVoiceEnabled: isVoiceKnowledgeCoachingEnabled,
  });
  const brandOptions = brandGroups.flatMap(({ brands, category }) =>
    brands.map(brand => ({
      category,
      label: brand.brand_name,
      value: brand.brand_id,
    })),
  );

  const [{ value: isActive }, , { setValue: setIsActive }] = useField<boolean>({
    name: 'is_active',
  });
  const [{ value: brands }, , { setValue: setBrands }] = useField<string[]>({
    name: 'brands',
  });
  const [{ value: products }, , { setValue: setProducts }] = useField<string[]>(
    {
      name: 'products',
    },
  );

  const [{ value: instructions }] = useField<string>({
    name: 'instructions',
  });
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          mb: 3,
        }}
      >
        {!isNew && (
          <Toggle
            checked={isActive}
            label={
              isActive
                ? t('aiInstruction.form.toggleOn')
                : t('aiInstruction.form.toggleOff')
            }
            onChange={() => setIsActive(!isActive)}
          />
        )}
        <Typography variant='font14'>
          <Trans
            components={{
              bold: <Typography variant='font14Bold'>{''}</Typography>,
            }}
            i18nKey='aiInstruction.form.intro'
            ns='knowledge'
          />
        </Typography>
        <Box
          sx={{
            borderTop: '1px solid rgba(0, 0, 0, 0.10)',
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <KnowledgeTextField
          description={t('aiInstruction.form.nameDescription')}
          label={t('aiInstruction.form.nameLabel')}
          name='name'
          placeholder={t('aiInstruction.form.namePlaceholder')}
        />
        {shouldEditBrands && (
          <FormInputFormat
            description={t('aiInstruction.form.brandDescription')}
            label={t('aiInstruction.form.brandLabel')}
          >
            <SelectDropdown
              id=''
              multiple
              onChange={e => {
                const value = e.target.value;
                if (!Array.isArray(value)) {
                  throw new Error('Value not valid');
                }

                setBrands(value);
              }}
              onClear={() => setBrands([])}
              options={brandOptions}
              placeholder={t('aiInstruction.form.brandPlaceholder')}
              value={brands}
            />
          </FormInputFormat>
        )}
        {shouldEditProducts && (
          <FormInputFormat
            description={t('aiInstruction.form.channelDescription')}
            label={t('aiInstruction.form.channelLabel')}
          >
            <SelectDropdown
              id=''
              multiple
              onChange={e => {
                const value = e.target.value;
                if (!Array.isArray(value)) {
                  throw new Error('Value not valid');
                }

                setProducts(value);
              }}
              onClear={() => setProducts([])}
              options={supportedChannels.map(product => ({
                label: formatProductLabel(product),
                value: product,
              }))}
              placeholder={t('aiInstruction.form.channelPlaceholder')}
              value={products}
            />
          </FormInputFormat>
        )}
        <KnowledgeTextField
          allowAddingContextVariables={allowAddingContextVariables}
          description={t('aiInstruction.form.instructionDescription')}
          inputDescription={t('aiInstruction.form.wordsRemaining', {
            limit: WORD_LIMIT,
            remaining: Math.max(WORD_LIMIT - wordCount(instructions), 0),
          })}
          label={
            <>
              {t('aiInstruction.form.instructionLabel')}{' '}
              <Tooltip tooltipContent={t('aiInstruction.form.tooltip')}>
                <IconInfoCircle size={16} />
              </Tooltip>
            </>
          }
          multiline
          name='instructions'
          placeholder={t('aiInstruction.form.instructionPlaceholder')}
          shouldUseContextVariableAutocomplete
        />
      </Box>
    </>
  );
};
